/**
 * ErDiagramBridge — postMessage bridge between the ER diagram iframe (served
 * by app_runner, source in priv/er_diagram/) and the host LiveView.
 *
 * Protocol v:1
 *   Iframe → LiveView:
 *     {v:1, type:"ready"}
 *     {v:1, type:"node_selected", table}
 *
 *   LiveView → iframe (server pushes via push_event):
 *     {v:1, type:"load_manifest", manifest}
 *
 * The manifest is the same shape used by the LiveView (string-keyed):
 *   {"tables": [{"name", "description", "columns", "foreign_keys"}]}
 *
 * Usage:
 *   <iframe phx-hook="ErDiagramBridge"
 *           data-origin={origin}
 *           data-manifest={Jason.encode!(manifest)}
 *           src={preview_url} />
 *
 * The hook caches the manifest from `data-manifest` and pushes it as soon
 * as the iframe announces `ready`. If the parent LV pushes an update via
 * `push_event(socket, "er_diagram:load", %{id, manifest})`, the hook forwards
 * it to the iframe.
 */

const ErDiagramBridge = {
  mounted() {
    this.targetOrigin = this.el.dataset.origin || "*";
    this.manifest = this.parseManifest(this.el.dataset.manifest);
    this.iframeReady = false;
    this.wrap = this.el.closest(".er-diagram-wrap");
    if (this.wrap) this.wrap.dataset.erLoaded = "false";

    this.onMessage = (event) => this.handleInbound(event);
    window.addEventListener("message", this.onMessage);

    this.handleEvent("er_diagram:load", (payload) => {
      if (payload && payload.id && payload.id !== this.el.id) return;
      if (payload && payload.manifest) {
        this.manifest = payload.manifest;
        if (this.iframeReady) this.sendManifest();
      }
    });
  },

  updated() {
    const newOrigin = this.el.dataset.origin;
    if (newOrigin) this.targetOrigin = newOrigin;

    const next = this.parseManifest(this.el.dataset.manifest);
    if (next) {
      this.manifest = next;
      if (this.iframeReady) this.sendManifest();
    }
  },

  destroyed() {
    window.removeEventListener("message", this.onMessage);
  },

  handleInbound(event) {
    if (!this.isIframeWindow(event.source)) return;
    if (!this.isOriginAllowed(event.origin)) return;
    const msg = event.data;
    if (!msg || msg.v !== 1 || typeof msg.type !== "string") return;

    if (msg.type === "ready") {
      this.iframeReady = true;
      if (this.wrap) this.wrap.dataset.erLoaded = "true";
      if (this.manifest) this.sendManifest();
      return;
    }

    if (msg.type === "node_selected" && typeof msg.table === "string") {
      this.pushEvent("er_diagram:node_selected", { table: msg.table });
    }
  },

  sendManifest() {
    const iframe = this.el;
    if (!iframe.contentWindow) return;
    try {
      iframe.contentWindow.postMessage(
        { v: 1, type: "load_manifest", manifest: this.manifest },
        this.targetOrigin
      );
    } catch (err) {
      console.error("[ErDiagramBridge] postMessage failed:", err);
    }
  },

  parseManifest(raw) {
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  },

  isIframeWindow(source) {
    return source && source === this.el.contentWindow;
  },

  isOriginAllowed(origin) {
    if (this.targetOrigin === "*") return true;
    return origin === this.targetOrigin;
  },
};

export default ErDiagramBridge;
